import {
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Button,
  makeStyles,
  Theme,
} from "@material-ui/core";
import React, { useEffect, useState } from "react";
import {
  jiraApiRef,
  JIRA_PROJECT_KEY,
  useProjectEntity,
} from "@internal/plugin-jira-api";
import { Entity } from "@backstage/catalog-model";
import { useApi } from "@backstage/core";
import { JiraProjectResponse } from "@internal/plugin-jira-api";
import {
  Content,
  MissingAnnotationEmptyState,
  Progress,
} from "@backstage/core-components";
import { Spacer } from "@internal/plugin-utilities";
import { useIssueDescriptionFormatted } from "../../hooks/useIssueDescriptionFormatted";

const useStyles = makeStyles((_theme: Theme) => ({
  submitButton: {
    marginTop: "40px",
  },
  label: {
    marginBottom: "10px",
  },
  disclaimer: {
    textAlign: "center",
    margin: "0 auto",
  },
  success: {
    textAlign: "center",
    color: "lime",
  },
  error: {
    textAlign: "center",
    color: "red",
  },
  portal: {
    textAlign: "center",
  },
}));

export interface Props {
  entity: Entity;
}

export const IssueForm: React.FC<Props> = ({ entity }): JSX.Element => {
  const api = useApi(jiraApiRef);
  const classes = useStyles();

  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<any>();

  // Grab the portal/project key from the Owner or
  // project key from the entity
  const { projectKey, isPortal, portal } = useProjectEntity(entity);

  // Grab the project thats associated with the entity
  const [project, setProject] = useState<JiraProjectResponse>();
  // Grab the issue types from the project
  const [issueType, setIssueType] = useState<string>("");
  const [types, setTypes] = useState<any[]>([]);

  const [summary, setSummary] = useState<string>();
  const [description, setDescription] = useState<string>();
  const [success, setSuccess] = useState<any>();

  const issueDescription = useIssueDescriptionFormatted(
    description || "",
    entity
  );

  useEffect(() => {
    (async () => {
      const response = await api.getProjectByKey(projectKey);
      setProject(response);
    })();
  }, [setProject, api, projectKey]);

  useEffect(() => {
    if (project && types.length === 0) {
      const parsedTypes =
        (project.issueTypes &&
          project.issueTypes.filter(
            (it: JiraProjectResponse["issueTypes"][0]) => {
              return it.subtask === false;
            }
          )) ||
        [];

      // Pull in the Bug or Task type of issue first
      const bug = (parsedTypes as JiraProjectResponse["issueTypes"]).find(
        (it: JiraProjectResponse["issueTypes"][0]) =>
          it.name === "Bug" || it.name === "Task"
      );

      setTypes(parsedTypes);

      setIssueType(
        (bug && bug.id) ||
          (project && parsedTypes && parsedTypes[0] && parsedTypes[0].id) ||
          ""
      );

      setLoading(false);
    }
  }, [project, setIssueType, setTypes, types.length]);

  const handleIssueTypeChange = (
    _event: React.ChangeEvent<{}>,
    newValue: any
  ) => {
    setIssueType(newValue);
  };

  const handleSubmit = async (e: any) => {
    setLoading(true);
    if (
      !summary ||
      !issueType ||
      !issueDescription ||
      !description ||
      !project ||
      !project.id
    ) {
      setLoading(false);
      return;
    }

    e.preventDefault();

    const response = await api.createIssue({
      summary,
      description: issueDescription,
      issueType,
      projectId: project.id,
    });

    if (response.status === "error") {
      setError(response);
    } else {
      setSuccess(response.data);
    }
    setLoading(false);
  };

  const handleReset = () => {
    setLoading(true);
    setError(undefined);
    setSummary(undefined);
    setDescription(undefined);
    setSuccess(undefined);
    setLoading(false);
  };

  const resetButton = (
    <Button onClick={handleReset} fullWidth variant="outlined">
      Reset Form
    </Button>
  );

  if (isPortal) {
    return (
      <div className={classes.portal}>
        <>
          <p>
            <>
              The {entity?.spec?.owner} team utilizes a Jira portal for their
              intake process. Please visit{" "}
              <a href={portal} target="_blank">
                {portal}
              </a>{" "}
              to submit an issue
            </>
          </p>
          <Spacer variant="normal" />
          <a href={portal} target="_blank">
            <Button variant="outlined">Submit Jira Issue on Portal</Button>
          </a>
        </>
      </div>
    );
  }

  if (!projectKey) {
    return (
      <div>
        <div className={classes.error}>
          <p>
            Jira annotation is missing from this Component, and from the Owner
            of this Component.
          </p>
          <p>
            <a href="https://radiator.staging.kajabi.farm/docs/default/component/radiator-site/jira-api/">
              Click here for more information
            </a>
          </p>
        </div>
        <MissingAnnotationEmptyState annotation={JIRA_PROJECT_KEY} />
      </div>
    );
  }

  if (error) {
    return (
      <div className={classes.error}>
        {error.error}
        <Spacer variant="normal" />
        {resetButton}
      </div>
    );
  }
  if (loading) {
    return <Progress />;
  }
  if (success) {
    const url = `https://kajabi.atlassian.net/browse/${success.key}`;
    return (
      <div className={classes.success}>
        Thank you for submitting your issue report!
        <Spacer variant="double" />
        <a href={url} target="_blank">
          {url}
        </a>
        <Spacer variant="normal" />
        {resetButton}
      </div>
    );
  }

  const issueTypes = types.map((type: any) => {
    return (
      <MenuItem key={type.id} value={type.id}>
        {type.name}
      </MenuItem>
    );
  });

  return (
    <form>
      <Content>
        <Grid container>
          <Grid item xs={12}>
            <InputLabel htmlFor="summary" className={classes.label}>
              Add a summary for this Issue
            </InputLabel>
            <TextField
              fullWidth
              multiline
              required
              id="summary"
              name="summary"
              variant="filled"
              value={summary}
              onChange={(e: any) => setSummary(e.target.value)}
            />
            <Spacer variant="normal" />
          </Grid>
          <Grid item xs={12}>
            <InputLabel htmlFor="issueType" className={classes.label}>
              Type of Issue?
            </InputLabel>
            <Select
              labelId="issueType"
              value={issueType}
              label="Type of Issue?"
              onChange={handleIssueTypeChange}
              fullWidth
              required
            >
              {issueTypes}
            </Select>
            <Spacer variant="normal" />
          </Grid>
          <Grid item xs={12}>
            <InputLabel htmlFor="description" className={classes.label}>
              Description of the Issue:
            </InputLabel>
            <TextField
              fullWidth
              multiline
              minRows={5}
              required
              onChange={(e: any) => setDescription(e.target.value)}
            />
          </Grid>
        </Grid>
        <Button
          className={classes.submitButton}
          type="submit"
          variant="outlined"
          fullWidth
          onClick={handleSubmit}
        >
          Submit
        </Button>
        <Spacer variant="normal" />
        <div className={classes.disclaimer}>
          Will be added to the {projectKey} project. All fields required
        </div>
      </Content>
    </form>
  );
};
